import React, { useEffect, useState } from "react";
import { Avatar, Tooltip } from "@mui/material";
import { PostMeta } from "../postsMeta/postsMeta";
import {
  PostDataInterface,
  TradePostInterface,
} from "../../Helpers/backendResponseInterfaces";
import Images from "../filesDisplay/Images";
import { Comments } from "../postsMeta/comments";
import {
  DEFAULT_POST_TYPE_MAP,
  ID_TYPES,
  SUPPORTED_INDEXES,
} from "../../Helpers/constants";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import LoadingScreen from "../utils/loadingScreen";
import { useQuery, UseQueryResult } from "react-query";
import { getSinglePost } from "../../Helpers/backendHelper";
import { GET_SINGLE_POSTS } from "./queryKey";
import { useLocation, useParams } from "react-router-dom";
import { defaultDecodeID, formatCreatedAt } from "../../Helpers/utils";
import { Helmet } from "react-helmet";

interface NormalPostProps {
  content?: string; // Define props interface with 'post' property
}

interface TradePostProps {
  tradeData?: TradePostInterface;
}
interface PostProps {
  initialPost?: PostDataInterface; // Define props interface with 'post' property
  internalPostId?: number;
  isQuoted?: boolean;
}

const NormalPost: React.FC<NormalPostProps> = ({ content }) => (
  <div className="w-full hover:bg-slate-100 transition-all duration-300 rounded p-3 break-words">
    {content}
  </div>
);

const getStyleByTradeType = (tradeData?: TradePostInterface) => {
  if (tradeData?.option_type === "CE") {
    if (tradeData.trade_type === "Buy") {
      return {
        titleStyle: "bg-green-100 text-green-800",
        entryStyle: "bg-green-100 text-green-800",
        icon: <ArrowDropUpIcon color="success" />,
      };
    } else if (tradeData.trade_type === "Sell") {
      return {
        titleStyle: "bg-red-100 text-red-800",
        entryStyle: "bg-red-100 text-red-800",
        icon: <ArrowDropDownIcon color="error" />,
      };
    }
  } else if (tradeData?.option_type === "PE") {
    if (tradeData.trade_type === "Buy") {
      return {
        titleStyle: "bg-red-100 text-red-800",
        entryStyle: "bg-green-100 text-green-800",
        icon: <ArrowDropUpIcon color="success" />,
      };
    } else if (tradeData.trade_type === "Sell") {
      return {
        titleStyle: "bg-green-100 text-green-800",
        entryStyle: "bg-red-100 text-red-800",
        icon: <ArrowDropDownIcon color="error" />,
      };
    }
  }
  if (tradeData?.trade_type === "Buy") {
    return {
      titleStyle: "bg-green-100 text-green-800",
      entryStyle: "bg-green-100 text-green-800",
      icon: <ArrowDropUpIcon color="success" />,
    };
  }
  return {
    titleStyle: "bg-red-100 text-red-800",
    entryStyle: "bg-red-100 text-red-800",
    icon: <ArrowDropDownIcon color="error" />,
  };
};

const TradePost: React.FC<TradePostProps> = ({ tradeData }) => {
  const { titleStyle, entryStyle, icon } = getStyleByTradeType(tradeData);
  return (
    <div className="w-full cursor-default transition-all duration-300 rounded-lg p-3 break-words border">
      <div
        className={`my-2 font-semibold text-gray-500 p-1 rounded-lg text-center`}
      >
        Trade
      </div>
      <div
        className={`my-2 font-thin p-1 rounded-lg text-center ${titleStyle}`}
      >
        {tradeData?.instrument_type}
      </div>
      <div className="my-2 font-thin p-1">
        {tradeData && SUPPORTED_INDEXES.includes(tradeData?.instrument_type)
          ? "Stock"
          : "Index"}
        <span className="mx-1 p-1 rounded-lg hover:bg-gray-300 border font-semibold">
          {tradeData?.index}
        </span>
      </div>
      {tradeData?.instrument_type === "Option" && (
        <div className="my-2 p-1 font-thin">
          Strike
          <span className="mx-1 p-1 rounded-lg hover:bg-gray-300 border font-semibold hover:font-bold transition-all duration-300">
            {tradeData?.strike_price} {tradeData?.option_type}
          </span>
        </div>
      )}
      <div className="my-2 p-1 font-thin">
        Entry
        <Tooltip
          title={`${tradeData?.trade_type} @${tradeData?.entry}`}
          placement="top"
          className="bg-slate-100"
          arrow
        >
          <span
            className={`mx-1 p-1 rounded-lg hover:bg-gray-300 border font-semibold hover:font-bold transition-all duration-300 ${entryStyle}`}
          >
            {tradeData?.entry}
            {icon}
          </span>
        </Tooltip>
        {tradeData?.target && (
          <>
            Target
            <span className="mx-1 p-1 rounded-lg hover:bg-gray-300 border font-semibold hover:font-bold transition-all duration-300">
              {tradeData?.target}
            </span>
          </>
        )}
        {tradeData?.stop_loss && (
          <>
            SL
            <span className="mx-1 p-1 rounded-lg hover:bg-gray-300 border font-semibold hover:font-bold transition-all duration-300">
              {tradeData?.stop_loss}
            </span>
          </>
        )}
      </div>
    </div>
  );
};

const Post: React.FC<PostProps> = ({
  initialPost,
  internalPostId,
  isQuoted,
}) => {
  const location = useLocation();
  const { postId } = useParams();
  const [toFetchPost, setToFetchPost] = useState<boolean>(false);
  const [isCommentOpen, setIsCommentOpen] = useState<boolean>(false);
  const [comment, setComment] = useState<string>("");
  const [post, setPost] = useState<PostDataInterface | undefined>(initialPost);
  const [commentCount, setCommentCount] = useState<number>(
    post?.metadata.comments || 0,
  );
  const isMobile = window.innerWidth <= 900;

  useEffect(() => {
    if (internalPostId) {
      setToFetchPost(true);
    }
  }, [internalPostId]);

  useQuery({
    queryKey: [GET_SINGLE_POSTS, internalPostId || postId],
    enabled: toFetchPost,
    queryFn: () => getSinglePost(internalPostId || postId || "NA"),
    onSuccess: (data: { data: PostDataInterface }) => {
      if (toFetchPost) setPost(data.data);
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  }) as UseQueryResult<PostDataInterface, unknown>;

  useEffect(() => {
    if (postId) {
      const isValid = defaultDecodeID(postId, ID_TYPES.post);
      if (isValid) {
        setToFetchPost(true);
        if (!isQuoted) setIsCommentOpen(true);
        setCommentCount(post?.metadata.comments || 0);
      }
    }
  }, [isQuoted, post?.metadata.comments, postId]);

  if (!post) {
    return <LoadingScreen />;
  }

  const postTitle = post.post_type === "Normal" ? "Normal Post" : "Trade Post";
  const postDescription = post.metadata.created_at;
  const postImage = post.user.profile_pic_url;

  return (
    <>
      {/* <Helmet>
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@your_twitter_username" />
        <meta name="twitter:title" content={postTitle} />
        <meta name="twitter:description" content={postDescription} />
        <meta name="twitter:image" content={postImage} />

        <meta property="og:title" content={postTitle} />
        <meta property="og:description" content={postDescription} />
        <meta property="og:image" content={postImage} />
        <meta
          property="ia:markup_url"
          content={`https://${getFEURL()}/posts/${post.encoded_id}`}
        />
        <meta
          property="og:url"
          content={`https://${getFEURL()}/posts/${post.encoded_id}`}
        />
        <meta property="og:type" content="article" />
      </Helmet> */}

      <Helmet>
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={postTitle} />
        <meta name="twitter:description" content={postDescription} />
        <meta name="twitter:image" content={postImage} />
        <meta
          name="twitter:url"
          content={`${window.location.origin}${location.pathname}`}
        />
      </Helmet>

      <div
        className={`flex gap-4 items-center justify-center ${
          isQuoted
            ? "w-full"
            : internalPostId
            ? "w-1/2"
            : toFetchPost
            ? "w-7/12 mx-auto mt-12 flex-col"
            : isMobile
            ? "w-full flex-col"
            : "w-full flex-row"
        }`}
      >
        <div
          className={`flex flex-col items-start gap-2 w-full hover:bg-slate-50 transition-all duration-300 p-3 
          ${isCommentOpen ? "bg-slate-50 rounded-lg" : "bg-white"}
          ${isCommentOpen && !(toFetchPost || isMobile) ? "-ml-40 my-6" : ""}
          ${toFetchPost ? "border-8 border-slate-30 rounded-lg" : ""}`}
        >
          <div className="flex flex-row items-center gap-2 w-full">
            <Avatar
              sx={{ width: 24, height: 24 }}
              alt={post.user.name}
              src={post.user.profile_pic_url}
            />
            <div className="flex flex-col items-start justify-center flex-grow">
              <div className="text-base font-extralight">{post.user.name}</div>
              <div className="text-xs font-thin">
                {formatCreatedAt(
                  post.metadata.created_at,
                  post.metadata.since_time,
                )}
              </div>
            </div>
          </div>
          {DEFAULT_POST_TYPE_MAP.normal.includes(post.post_type) && (
            <NormalPost content={post.content} />
          )}
          {DEFAULT_POST_TYPE_MAP.trade.includes(post.post_type) && (
            <TradePost tradeData={post?.trade_data} />
          )}
          {post.post_uploads.length > 0 && (
            <Images images={post.post_uploads} />
          )}
          {/* <Skeleton variant="text" className="w-1/3 text-base" /> */}
          {post?.post_type === "Quote" && (
            <Post internalPostId={post.parent_post_id} isQuoted={true} />
          )}
          {!internalPostId && (
            <PostMeta
              postID={post.id}
              likes={post.metadata.likes}
              comments={commentCount}
              postIsLiked={post.is_liked}
              isCommentOpen={isCommentOpen}
              setIsCommentOpen={setIsCommentOpen}
            />
          )}
        </div>
        {isCommentOpen && (
          <Comments
            postId={post.id}
            setIsCommentsOpen={setIsCommentOpen}
            comment={comment}
            setComment={setComment}
            setCommentCount={setCommentCount}
            needMargin={!(toFetchPost || isMobile)}
          />
        )}
      </div>
    </>
  );
};

export default Post;
